'use client';

import { c as _c } from "react/compiler-runtime";
import type { PropsWithStyles } from '@morpho-org/ui-v6/types';
import type { Address } from 'viem';
import { formatLongString } from '@morpho-org/morpho-ts';
import { Avatar, ClipboardButton, InfoTooltip } from '@morpho-org/ui-v6/client';
import { cn } from '@morpho-org/ui-v6/utils';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Button, Switch, ThemeSegmentedControl } from '@repo/ui';
import { Logout, Wallet } from '@repo/ui/icons';
import { abbreviateAddress } from '@repo/utils';
import { type ForwardedRef, forwardRef } from 'react';
import { useWeb3Context } from '@/contexts/Web3Context';
import './WalletDropdown.css';

/* eslint-disable better-tailwindcss/no-unregistered-classes -- `wallet-dropdown-slide-up-and-fade-out` is a component-scoped class defined in WalletDropdown.css */

export interface WalletDropdownProps extends PropsWithStyles {
  user: {
    address: Address;
    ens?: string;
    ensImage?: string;
  };
  trigger: React.ReactNode;
  rootProps?: React.ComponentProps<typeof DropdownMenu.Root>;
  triggerProps?: React.ComponentProps<typeof DropdownMenu.Trigger>;
  portalProps?: React.ComponentProps<typeof DropdownMenu.Portal>;
  contentProps?: React.ComponentProps<typeof DropdownMenu.Content>;
  positionRelativeToViewport?: boolean;
}
const WalletDropdown = (t0, ref) => {
  const $ = _c(63);
  const {
    user: t1,
    trigger,
    rootProps,
    triggerProps,
    portalProps,
    contentProps,
    positionRelativeToViewport: t2
  } = t0;
  const {
    ens,
    address,
    ensImage
  } = t1;
  const positionRelativeToViewport = t2 === undefined ? true : t2;
  const {
    account,
    modal,
    allowSignatureRequirements,
    onAllowSignatureRequirementsChange
  } = useWeb3Context();
  const t3 = positionRelativeToViewport && "fixed top-[84px] right-md";
  const t4 = contentProps?.className;
  let t5;
  if ($[0] !== t3 || $[1] !== t4) {
    t5 = cn("z-50 flex w-[min(350px,95vw)] flex-col gap-md overflow-hidden rounded-[20px] border-[0.5px] border-border bg-popover px-md py-lg shadow-lg", "data-[state=open]:animate-slide-down-and-fade data-[state=closed]:wallet-dropdown-slide-up-and-fade-out", "max-sm:w-[calc(100vw-40px)]", t3, t4);
    $[0] = t3;
    $[1] = t4;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const t6 = ensImage ?? undefined;
  let t7;
  if ($[3] !== address || $[4] !== t6) {
    t7 = <Avatar size="medium" address={address} ensUrl={t6} />;
    $[3] = address;
    $[4] = t6;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      maxWidth: 120,
      lineHeight: "normal"
    };
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] !== address || $[8] !== ens) {
    t9 = ens ? formatLongString(ens, 20) : abbreviateAddress(address);
    $[7] = address;
    $[8] = ens;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== t9) {
    t10 = <span className="truncate text-sm" style={t8}>{t9}</span>;
    $[10] = t9;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== address) {
    t11 = <ClipboardButton toCopy={address} toastMessage="Address copied to clipboard." tooltipMessage="Copy address." size="sm" />;
    $[12] = address;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== t10 || $[15] !== t11) {
    t12 = <div className="gap-2xs flex items-center">{t10}{t11}</div>;
    $[14] = t10;
    $[15] = t11;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== t12 || $[18] !== t7) {
    t13 = <div className="gap-xs flex items-center">{t7}{t12}</div>;
    $[17] = t12;
    $[18] = t7;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== modal) {
    t14 = () => {
      modal.open();
    };
    $[20] = modal;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Wallet />;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== t14) {
    t16 = <Button variant="ghost" size="icon" aria-label="Open wallet" onClick={t14}>{t15}</Button>;
    $[23] = t14;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  let t17;
  if ($[25] !== modal) {
    t17 = () => modal.disconnect();
    $[25] = modal;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <Logout />;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  let t19;
  if ($[28] !== t17) {
    t19 = <Button variant="ghost" size="icon" aria-label="Disconnect wallet" onClick={t17}>{t18}</Button>;
    $[28] = t17;
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  let t20;
  if ($[30] !== t16 || $[31] !== t19) {
    t20 = <div className="gap-xs flex items-center">{t16}{t19}</div>;
    $[30] = t16;
    $[31] = t19;
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  let t21;
  if ($[33] !== t13 || $[34] !== t20) {
    t21 = <div className="flex items-center justify-between">{t13}{t20}</div>;
    $[33] = t13;
    $[34] = t20;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  let t22;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <div className="border-border border-b-[0.5px]" />;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  let t23;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <div className="flex items-center justify-between"><span className="text-foreground text-xs" style={{
        lineHeight: "normal"
      }}>Theme</span><ThemeSegmentedControl className="w-fit" /></div>;
    $[37] = t23;
  } else {
    t23 = $[37];
  }
  let t24;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <span className="text-foreground text-xs">Allow offchain signature flows</span>;
    $[38] = t24;
  } else {
    t24 = $[38];
  }
  let t25;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <div className="gap-xs flex items-center">{t24}<InfoTooltip tooltipContent={<>Use offchain authorizations (permit, permit2, etc.) instead of onchain approvals. Disable if your wallet doesn’t support offchain signatures.</>} /></div>;
    $[39] = t25;
  } else {
    t25 = $[39];
  }
  const t26 = allowSignatureRequirements && !account.isSafe && !account.isContract;
  let t27;
  if ($[40] !== account.isContract || $[41] !== onAllowSignatureRequirementsChange || $[42] !== t26) {
    t27 = <div className="flex items-center justify-between">{t25}<Switch checked={t26} disabled={account.isContract} onCheckedChange={onAllowSignatureRequirementsChange} /></div>;
    $[40] = account.isContract;
    $[41] = onAllowSignatureRequirementsChange;
    $[42] = t26;
    $[43] = t27;
  } else {
    t27 = $[43];
  }
  let t28;
  if ($[44] !== contentProps || $[45] !== ref || $[46] !== t21 || $[47] !== t27 || $[48] !== t5) {
    t28 = <DropdownMenu.Content ref={ref} {...contentProps} className={t5}>{t21}{t22}{t23}{t27}</DropdownMenu.Content>;
    $[44] = contentProps;
    $[45] = ref;
    $[46] = t21;
    $[47] = t27;
    $[48] = t5;
    $[49] = t28;
  } else {
    t28 = $[49];
  }
  const content = t28;
  let t29;
  if ($[50] !== content || $[51] !== positionRelativeToViewport) {
    t29 = positionRelativeToViewport ? <div className="flex [&_[data-radix-popper-content-wrapper]]:!relative [&_[data-radix-popper-content-wrapper]]:![transform:none] [&_[data-radix-popper-content-wrapper]]:![will-change:initial]">{content}</div> : content;
    $[50] = content;
    $[51] = positionRelativeToViewport;
    $[52] = t29;
  } else {
    t29 = $[52];
  }
  const wrappedContent = t29;
  let t30;
  if ($[53] !== trigger || $[54] !== triggerProps) {
    t30 = <DropdownMenu.Trigger asChild={true} {...triggerProps}>{trigger}</DropdownMenu.Trigger>;
    $[53] = trigger;
    $[54] = triggerProps;
    $[55] = t30;
  } else {
    t30 = $[55];
  }
  let t31;
  if ($[56] !== portalProps || $[57] !== wrappedContent) {
    t31 = <DropdownMenu.Portal {...portalProps}>{wrappedContent}</DropdownMenu.Portal>;
    $[56] = portalProps;
    $[57] = wrappedContent;
    $[58] = t31;
  } else {
    t31 = $[58];
  }
  let t32;
  if ($[59] !== rootProps || $[60] !== t30 || $[61] !== t31) {
    t32 = <DropdownMenu.Root {...rootProps} modal={false}>{t30}{t31}</DropdownMenu.Root>;
    $[59] = rootProps;
    $[60] = t30;
    $[61] = t31;
    $[62] = t32;
  } else {
    t32 = $[62];
  }
  return t32;
};
export default forwardRef(WalletDropdown);